import { Map } from "immutable";

import { Action } from '../actions';
import { DEFAULT_PROCESS_STEP, EPSILON, NoteColor, NoteType, ProcessStep, } from "../constants";
import { BoundsType, ShapeData } from "../services/shape-service";
import { createSlidePathData } from "../services/slide_helper";


export interface Note {
    readonly uuid: string,
    readonly x: number,
    readonly y: number,
    readonly text: string,
    readonly color: NoteColor,
    readonly type: NoteType,
}

export interface SlidePathData {
    frame: string,
    lines: string,
}

export interface SlideData {
    readonly uuid: string,
    readonly x: number,
    readonly y: number,
    readonly notes: string,
    readonly notesLines: ReadonlyArray<string>,
    readonly pathData: SlidePathData,
    readonly shapes: ReadonlyArray<ShapeData>,
}

export interface BasicData {
    readonly goals: string,
    readonly mainMessage: string,
    readonly audience: string,
}

export interface Presentation {
    readonly id: number,
    readonly processStep: ProcessStep,
    readonly basicData: BasicData,
    readonly templateId: number,
    readonly notes: ReadonlyArray<Note>,
    readonly slides: ReadonlyArray<SlideData>,
    readonly textFields: Map<string, string>,
}


const initialState: Presentation = {
    id: -1,
    processStep: DEFAULT_PROCESS_STEP,
    basicData: {
        goals: '',
        mainMessage: '',
        audience: '',
    },
    templateId: -1,
    notes: [],
    slides: [],
    textFields: Map(),
};

export default function handleAction(state: Presentation = initialState, action: Action): Presentation {
    switch (action.type) {

        case 'SET_PRESENTATION':
            return action.presentation;

        case 'SETUP_STORYBOARD':
            return {
                ...state,
                processStep: ProcessStep.SKETCH,
                slides: action.slides.map(slide => ({
                    ...slide,
                    notes: '',
                    notesLines: [],
                    pathData: createSlidePathData(),
                    shapes: [],
                })),
            };

        case 'SET_PROCESS_STEP':
            return {
                ...state,
                processStep: action.newProcessStep,
            };

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                templateId: action.newTemplateId,
                notes: state.notes.map(
                    note => ({
                        ...note,
                        x: note.x * action.canvasWidthRatio,
                        y: note.y * action.canvasHeightRatio,
                    })
                ),
                slides: state.slides.map(
                    slide => ({
                        ...slide,
                        x: slide.x * action.storyboardWidthRatio,
                        y: slide.y * action.storyboardHeightRatio,
                    })
                ),
            };

        case 'START_NOTE_DRAGGING': {
            const draggedNote = state.notes.find(note => note.uuid === action.uuid);
            return draggedNote ?
                {
                    ...state,
                    notes: [...state.notes.filter(note => note.uuid !== action.uuid), draggedNote]
                }
                : state;
        }

        case 'NOTE_DRAGGING':
        case 'STOP_NOTE_DRAGGING':
        case 'MOVE_NOTE':
            return {
                ...state,
                notes: state.notes.map(
                    note => note.uuid !== action.uuid ? note : {...note, x: action.endX, y: action.endY}
                )
            };

        case 'START_SLIDE_DRAGGING': {
            const draggedSlide = state.slides.find(slide => slide.uuid === action.uuid);
            return draggedSlide ?
                {
                    ...state,
                    slides: [...state.slides.filter(slide => slide.uuid !== action.uuid), draggedSlide]
                }
                : state;
        }

        case 'SLIDE_DRAGGING':
        case 'STOP_SLIDE_DRAGGING':
        case 'MOVE_SLIDE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.uuid ? slide : {...slide, x: action.endX, y: action.endY}
                )
            };

        case 'START_SHAPE_SKETCHING': {
            const oldSlide = state.slides.find(slide => slide.uuid === action.slideUuid);
            if (!oldSlide) {
                return state;
            }
            const newSlide = {
                ...oldSlide,
                shapes: [...oldSlide.shapes, action.shape],
            };
            return {
                ...state,
                slides: [...state.slides.filter(slide => slide.uuid !== action.slideUuid), newSlide],
            };
        }

        case 'SHAPE_SKETCHING':
        case 'STOP_SHAPE_SKETCHING':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => {
                                    if (shape.uuid !== action.shapeUuid) {
                                        return shape;
                                    }
                                    let newShape = {
                                        ...shape,
                                        x1: action.x1,
                                        y1: action.y1,
                                        x2: action.x2,
                                        y2: action.y2,
                                        path: action.path,
                                    };
                                    switch (newShape.type) {
                                        case 'FREEFORM':
                                            newShape = { ...newShape, points: action.points };
                                            break;
                                        case 'TEXT':
                                            const boundsType = Math.abs(action.x2-action.x1) < EPSILON && Math.abs(action.y2 - action.y1) < EPSILON? BoundsType.DYNAMIC : BoundsType.FIXED;
                                            newShape = {
                                                ...newShape,
                                                boundsType,
                                                lines: action.textLines,
                                                size: action.fontSize,
                                            };
                                            break;
                                    }
                                    return newShape;
                                }
                            ),
                        }
                ),
            };

        case 'CREATE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: [...slide.shapes, action.shape],
                        },
                )
            };

        case 'DELETE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.filter(shape => shape.uuid !== action.shape.uuid),
                        },
                )
            };

        case 'START_SHAPE_DRAGGING': {
            const oldSlide = state.slides.find(slide => slide.uuid === action.slideUuid);
            if (!oldSlide) {
                return state;
            }
            const draggedShape = oldSlide.shapes.find(shape => shape.uuid === action.shapeUuid);
            if (!draggedShape) {
                return state;
            }
            const newSlide = {
                ...oldSlide,
                shapes: [...oldSlide.shapes.filter(shape => shape.uuid !== action.shapeUuid), draggedShape]
            };
            return {
                ...state,
                slides: [...state.slides.filter(slide => slide.uuid !== action.slideUuid), newSlide],
            };
        }

        case 'START_SELECTOR_DRAGGING': {
            const oldSlide = state.slides.find(slide => slide.uuid === action.slideUuid);
            if (!oldSlide) {
                return state;
            }
            const draggedShape = oldSlide.shapes.find(shape => shape.uuid === action.shape.uuid);
            if (!draggedShape) {
                return state;
            }
            const newSlide = {
                ...oldSlide,
                shapes: [...oldSlide.shapes.filter(shape => shape.uuid !== action.shape.uuid), draggedShape]
            };
            return {
                ...state,
                slides: [...state.slides.filter(slide => slide.uuid !== action.slideUuid), newSlide],
            };
        }

        case 'SHAPE_DRAGGING':
        case 'STOP_SHAPE_DRAGGING':
        case 'MOVE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => shape.uuid !== action.shapeUuid ? shape :
                                    {
                                        ...shape,
                                        x1: action.endX,
                                        y1: action.endY,
                                        x2: action.endX + shape.x2 - shape.x1,
                                        y2: action.endY + shape.y2 - shape.y1,
                                    }),
                        }
                ),
            };

        case 'SELECTOR_DRAGGING':
        case 'STOP_SELECTOR_DRAGGING':
        case 'RESIZE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => {
                                    if (shape.uuid !== action.shapeUuid) {
                                        return shape;
                                    }
                                    let newShape = {
                                        ...shape,
                                        x1: action.endX1,
                                        y1: action.endY1,
                                        x2: action.endX2,
                                        y2: action.endY2,
                                        path: action.endPath,
                                    };
                                    if (newShape.type === 'FREEFORM') {
                                        newShape = { ...newShape, points: action.endPoints || [] };
                                    }
                                    if (newShape.type === 'TEXT') {
                                        newShape = {
                                            ...newShape,
                                            boundsType: BoundsType.FIXED,
                                            lines: action.endLines,
                                            size: action.endSize,
                                        };
                                    }
                                    return newShape;
                                }
                            ),
                        }
                ),
            };

        case 'UPDATE_TEXT_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => shape.uuid !== action.shapeUuid ? shape :
                                    {
                                        ...shape,
                                        text: action.newText,
                                        lines: action.newLines,
                                        size: action.newSize,
                                        x1: action.newX1,
                                        y1: action.newY1,
                                        x2: action.newX2,
                                        y2: action.newY2,

                                    }
                            )
                        }
                )
            };

        case 'CREATE_NOTE':
            return {
                ...state,
                notes: [
                    ...state.notes,
                    {
                        uuid: action.uuid,
                        text: action.text,
                        color: action.color,
                        type: action.noteType,
                        x: action.noteX,
                        y: action.noteY,
                    } as Note
                ]
            };

        case 'UPDATE_NOTE':
            return {
                ...state,
                notes: state.notes.map(
                    note => note.uuid === action.uuid ?
                        {
                            ...note,
                            text: action.newText,
                            color: action.newColor
                        }
                        : note
                )
            };

        case 'DELETE_NOTE':
            return {
                ...state,
                notes: state.notes.filter(note => note.uuid !== action.uuid)
            };

        case 'CREATE_SLIDE':
            return {
                ...state,
                slides: [
                    ...state.slides,
                    {
                        uuid: action.uuid,
                        x: action.slideX,
                        y: action.slideY,
                        notes: '',
                        notesLines: [],
                        pathData: createSlidePathData(),
                        shapes: [],
                    }
                ]
            };

        case 'UPDATE_SLIDE_NOTES':
            return {
                ...state,
                slides: [
                    ...state.slides.map(
                        slide => slide.uuid !== action.uuid ? slide :
                            {
                                ...slide,
                                notes: action.newText,
                                notesLines: action.newLines,
                            }
                    )
                ]
            };

        case 'DELETE_SLIDE':
            return {
                ...state,
                slides: state.slides.filter(slide => slide.uuid !== action.slide.uuid)
            };

        case 'SET_GOALS':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    goals: action.newGoals,
                }
            };

        case 'SET_MAIN_MESSAGE':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    mainMessage: action.newMainMessage,
                }
            };

        case 'SET_AUDIENCE':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    audience: action.newAudience,
                }
            };

        case 'SET_TEXT_FIELD':
            return {
                ...state,
                textFields: state.textFields.set(action.key, action.newValue)
            };

        case 'UNDO':
            return handleUndo(state, action.undoAction);

        case 'REDO':
            return handleAction(state, action.redoAction);

        default:
            return state;
    }
}

function handleUndo(state: Presentation, action: Action): Presentation {
    switch (action.type) {

        case 'SETUP_STORYBOARD':
            return {
                ...state,
                processStep: ProcessStep.STRUCTURE,
                slides: [],
            };

        case 'SET_PROCESS_STEP':
            return {
                ...state,
                processStep: action.oldProcessStep,
            };

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                templateId: action.oldTemplateId,
                notes: state.notes.map(
                    note => ({
                        ...note,
                        x: note.x / action.canvasWidthRatio,
                        y: note.y / action.canvasHeightRatio,
                    })
                ),
                slides: state.slides.map(
                    slide => ({
                        ...slide,
                        x: slide.x / action.storyboardWidthRatio,
                        y: slide.y / action.storyboardHeightRatio,
                    })
                ),
            };

        case 'CREATE_NOTE':
            return {
                ...state,
                notes: state.notes.filter(note => note.uuid !== action.uuid)
            };

        case 'MOVE_NOTE':
            return {
                ...state,
                notes: state.notes.map(
                    note => note.uuid !== action.uuid ? note : {...note, x: action.startX, y: action.startY}
                )
            };

        case 'UPDATE_NOTE':
            return {
                ...state,
                notes: state.notes.map(
                    note => note.uuid === action.uuid ?
                        {
                            ...note,
                            text: action.oldText,
                            color: action.oldColor
                        }
                        : note
                )
            };

        case 'DELETE_NOTE':
            return {
                ...state,
                notes: state.notes.concat({
                    uuid: action.uuid,
                    text: action.text,
                    color: action.color,
                    type: action.noteType,
                    x: action.noteX,
                    y: action.noteY,
                })
            };

        case 'CREATE_SLIDE':
            return {
                ...state,
                slides: state.slides.filter(slide => slide.uuid !== action.uuid)
            };

        case 'UPDATE_SLIDE_NOTES':
            return {
                ...state,
                slides: [
                    ...state.slides.map(
                        slide => slide.uuid !== action.uuid ? slide :
                            {
                                ...slide,
                                notes: action.oldText,
                                notesLines: action.oldLines,
                            }
                    )
                ]
            };

        case 'MOVE_SLIDE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.uuid ? slide : {...slide, x: action.startX, y: action.startY}
                )
            };

        case 'DELETE_SLIDE':
            return {
                ...state,
                slides: state.slides.concat(action.slide)
            };


        case 'CREATE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.filter(shape => shape.uuid !== action.shape.uuid),
                        },
                )
            };

        case 'DELETE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: [...slide.shapes, action.shape],
                        }
                )
            };

        case 'MOVE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(slide => slide.uuid !== action.slideUuid ? slide :
                    {
                        ...slide,
                        shapes: slide.shapes.map(shape => shape.uuid !== action.shapeUuid ? shape :
                            {
                                ...shape,
                                x1: action.startX,
                                y1: action.startY,
                                x2: action.startX + shape.x2 - shape.x1,
                                y2: action.startY + shape.y2 - shape.y1,
                            }
                        )
                    }
                )
            };

        case 'RESIZE_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => {
                                    if (shape.uuid !== action.shapeUuid) {
                                        return shape;
                                    }
                                    let newShape = {
                                        ...shape,
                                        x1: action.startX1,
                                        y1: action.startY1,
                                        x2: action.startX2,
                                        y2: action.startY2,
                                        path: action.startPath,
                                    };
                                    if (newShape.type === 'FREEFORM') {
                                        newShape = { ...newShape, points: action.startPoints || [] };
                                    }
                                    if (newShape.type === 'TEXT') {
                                        newShape = {
                                            ...newShape,
                                            boundsType: action.startBoundsType,
                                            lines: action.startLines,
                                            size: action.startSize,
                                        };
                                    }
                                    return newShape;
                                }
                            ),
                        }
                ),
            };

        case 'UPDATE_TEXT_SHAPE':
            return {
                ...state,
                slides: state.slides.map(
                    slide => slide.uuid !== action.slideUuid ? slide :
                        {
                            ...slide,
                            shapes: slide.shapes.map(
                                shape => shape.uuid !== action.shapeUuid ? shape :
                                    {
                                        ...shape,
                                        text: action.oldText,
                                        lines: action.oldLines,
                                        size: action.oldSize,
                                        x1: action.oldX1,
                                        y1: action.oldY1,
                                        x2: action.oldX2,
                                        y2: action.oldY2,
                                    }
                            )
                        }
                )
            };

        case 'SET_GOALS':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    goals: action.oldGoals,
                }
            };

        case 'SET_MAIN_MESSAGE':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    mainMessage: action.oldMainMessage,
                }
            };

        case 'SET_AUDIENCE':
            return {
                ...state,
                basicData: {
                    ...state.basicData,
                    audience: action.oldAudience,
                }
            };

        case 'SET_TEXT_FIELD':
            return {
                ...state,
                textFields: state.textFields.set(action.key, action.oldValue)
            };

        default:
            return state;
    }
}

export const getPresentationId = (state: Presentation): number => state.id;
export const getProcessStep = (state: Presentation): ProcessStep => state.processStep;
export const getBasicData = (state: Presentation): BasicData => state.basicData;
export const getTemplateId = (state: Presentation): number => state.templateId;
export const getNotes = (state: Presentation): ReadonlyArray<Note> => state.notes;
export const getSlides = (state: Presentation): ReadonlyArray<SlideData> => state.slides;
export const getTextFieldValue = (state: Presentation, textFieldKey: string): string => state.textFields.get(textFieldKey, '');
export const getTextFields = (state: Presentation): Map<string, string> => state.textFields;


// WEBPACK FOOTER //
// ./src/reducers/presentation.ts